import React, { useState } from 'react';
import { Globe, Lock, Trash2, BookOpen } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


const TCG_COLORS = {
  pokemon: 'text-[#D4A84B]', magic: 'text-[#0D5E4A]', yugioh: 'text-[#C45B4A]',
  onepiece: 'text-[#EF4444]', riftbound: 'text-[#0BC4E2]',
};

export default function AdminCollections({ collections, setCollections, lang, setMessage }) {
  const [showPrivate, setShowPrivate] = useState(false);
  const l = createTextPicker(lang);

  const handleDelete = async (id, name) => {
    if (!window.confirm(l(`¿Eliminar la colección "${name}"?`, `Delete collection "${name}"?`))) return;
    try {
      await axios.delete(`${API}/admin/collections/${id}`);
      setCollections(prev => prev.filter(c => c.collection_id !== id));
      setMessage(l('Colección eliminada', 'Collection deleted'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const handleToggleVisibility = async (id, currentlyPublic) => {
    try {
      await axios.patch(`${API}/admin/collections/${id}/visibility`, null, {
        params: { is_public: !currentlyPublic }
      });
      setCollections(prev => prev.map(c =>
        c.collection_id === id ? { ...c, is_public: !currentlyPublic } : c
      ));
      setMessage(currentlyPublic ? l('Colección ocultada', 'Collection hidden') : l('Colección publicada', 'Collection published'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const visible = collections.filter(c => showPrivate ? true : c.is_public);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-xl">
          {l('Colecciones', 'Collections')} ({visible.length})
        </h2>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showPrivate} onChange={e => setShowPrivate(e.target.checked)}
            className="w-4 h-4 accent-[#C9A962]" />
          <span className="text-[#A1A1AA] text-sm">{l('Mostrar privadas', 'Show private')}</span>
        </label>
      </div>

      {visible.length === 0 ? (
        <div className="text-center py-10 text-[#52525B]">
          <BookOpen size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">{l('No hay colecciones públicas', 'No public collections')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map(c => (
            <div key={c.collection_id} className="bg-[#12121A] border border-white/5 rounded-xl px-4 py-3 flex items-center gap-4">
              <BookOpen size={16} className="text-[#A1A1AA] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm truncate">{c.name}</p>
                  <span className={`text-xs font-medium ${TCG_COLORS[c.tcg_type] || 'text-[#A1A1AA]'}`}>{c.tcg_type}</span>
                  {!c.is_public && <span className="px-1.5 py-0.5 bg-white/5 text-[#52525B] rounded text-[10px]">{l('Privada', 'Private')}</span>}
                </div>
                <p className="text-[#52525B] text-xs">
                  {c.owner_name} · {c.card_count} {l('cartas', 'cards')}
                  {c.description && ` · ${c.description.slice(0, 40)}...`}
                </p>
              </div>
              <button
                onClick={() => handleToggleVisibility(c.collection_id, c.is_public)}
                className={`p-1.5 rounded-lg transition-colors ${
                  c.is_public ? 'text-[#10B981] hover:bg-green-500/10' : 'text-[#A1A1AA] hover:bg-white/5'
                }`}
                title={c.is_public ? l('Ocultar', 'Hide') : l('Publicar', 'Publish')}
              >
                {c.is_public ? <Globe size={14} /> : <Lock size={14} />}
              </button>
              <button
                onClick={() => handleDelete(c.collection_id, c.name)}
                className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10"
                title={l('Eliminar', 'Delete')}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
